import React, { useEffect, useRef, useState } from 'react';
import useMediaStreamTrack from '../../hooks/useMediaStreamTrack';
import useVideoContext from '../../hooks/useVideoContext';
import useLocalAudioToggle from '../../hooks/useLocalAudioToggle';
import { Logger } from '../../utils';

const AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext;

function initializeAnalyser(stream) {
    audioContext = audioContext || new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(stream);

    const analyser = audioContext.createAnalyser();
    analyser.smoothingTimeConstant = 0.4;
    analyser.fftSize = 512;

    audioSource.connect(analyser);
    return analyser;
}

function AudioVisualizer() {
    const { localTracks } = useVideoContext();
    const audioTrack = localTracks.find(track => track.kind === 'audio');
    const [analyser, setAnalyser] = useState();
    const mediaStreamTrack = useMediaStreamTrack(audioTrack);
    const [volume, setVolume] = useState();
    const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
    const timerId = useRef();


    useEffect(() => {
        if (audioTrack && mediaStreamTrack && isAudioEnabled) {
            // Here we create a new MediaStream from a clone of the mediaStreamTrack.
            // A clone is created to allow multiple instances of this component for a single
            // AudioTrack on iOS Safari.
            let newMediaStream = new MediaStream([mediaStreamTrack.clone()]);

            // Here we listen for the 'stopped' event on the audioTrack. When the audioTrack is stopped,
            // we stop the cloned track that is stored in 'newMediaStream'. It is important that we stop
            // all tracks when they are not in use. Browsers like Firefox don't let you create a new stream
            // from a new audio device while the active audio device still has active tracks.
            const stopAllMediaStreamTracks = () => newMediaStream.getTracks().forEach(track => track.stop());
            audioTrack.on('stopped', stopAllMediaStreamTracks);

            const reinitializeAnalyser = () => {
                stopAllMediaStreamTracks();
                newMediaStream = new MediaStream([mediaStreamTrack.clone()]);
                setAnalyser(initializeAnalyser(newMediaStream));
            };

            setAnalyser(initializeAnalyser(newMediaStream));

            // Here we reinitialize the AnalyserNode on focus to avoid an issue in Safari
            // where the analysers stop functioning when the user switches to a new tab
            // and switches back to the app.
            window.addEventListener('focus', reinitializeAnalyser);

            return () => {
                stopAllMediaStreamTracks();
                window.removeEventListener('focus', reinitializeAnalyser);
                audioTrack.off('stopped', stopAllMediaStreamTracks);
            };
        }
    }, [isAudioEnabled, mediaStreamTrack, audioTrack]);

    useEffect(() => {
        if (analyser) {
            const sampleArray = new Uint8Array(analyser.frequencyBinCount);
            const getMicVolume = () => {
                timerId.current = requestAnimationFrame(getMicVolume);
                analyser.getByteFrequencyData(sampleArray);
                let values = 0;
                const length = sampleArray.length;
                for (let i = 0; i < length; i++) {
                    values += sampleArray[i];
                }
                const volume = Math.min(21, Math.max(0, Math.log10(values / length / 3) * 14));
                setVolume(volume);
            };
            getMicVolume();
        }
        return () => { cancelAnimationFrame(timerId.current); }
    }, [analyser]);

    const audioBtnClick = () => {
        Logger(`UI.Twilio.Settings.Audio.${isAudioEnabled ? "Mute" : "Unmute"}.Clicked`, new Date());
        toggleAudioEnabled();
    };

    return (
        isAudioEnabled ? <React.Fragment>
            <span className="icon-line-mic" onClick={audioBtnClick} />
            <div className="audio-visualizer">
                {[...Array(18)].map((x, i) =>
                    <span className={i <= volume ? "audio-bar active" : "audio-bar"} key={i} />
                )}
            </div>
        </React.Fragment> : <React.Fragment>
                <span className="icon-line-mic-off" onClick={audioBtnClick} />
                <div className="audio-visualizer">
                    {[...Array(18)].map((x, i) =>
                        <span className="audio-bar" key={i} />
                    )}
                </div>
            </React.Fragment>

    );
}

export default React.memo(AudioVisualizer);
